/**
 * Patients pasted from a spreadsheet or a CSV file: one per line, a name and an email address in
 * any order, separated by commas, tabs or semicolons. `Robert James <robert@example.com>` works too.
 * A first line naming the columns is skipped.
 */

const EMAIL_IN_LINE = /<?([^\s<>@,;"]+@[^\s<>@,;"]+\.[^\s<>@,;"]{2,})>?/;

export interface ParsedPatient {
  name: string;
  email: string;
}

export interface ParseProblem {
  line: number;
  text: string;
  reason: string;
}

export function parsePatientLines(text: string): { patients: ParsedPatient[]; problems: ParseProblem[] } {
  const patients: ParsedPatient[] = [];
  const problems: ParseProblem[] = [];
  const seen = new Set<string>();
  let first = true;
  text.split(/\r?\n/).forEach((raw, i) => {
    const line = raw.trim();
    if (!line) return;
    const isFirst = first;
    first = false;
    const m = EMAIL_IN_LINE.exec(line);
    if (!m?.[1]) {
      if (isFirst && /e-?mail/i.test(line)) return;
      problems.push({ line: i + 1, text: line, reason: 'no email address' });
      return;
    }
    const email = m[1].toLowerCase();
    const name = line
      .replace(m[0], ' ')
      .replace(/["<>]/g, ' ')
      .split(/[,;\t]/)
      .map((c) => c.trim())
      .filter(Boolean)
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (!name) {
      problems.push({ line: i + 1, text: line, reason: 'no name' });
      return;
    }
    if (name.length > 60) {
      problems.push({ line: i + 1, text: line, reason: 'name is longer than 60 characters' });
      return;
    }
    if (seen.has(email)) {
      problems.push({ line: i + 1, text: line, reason: 'listed twice' });
      return;
    }
    seen.add(email);
    patients.push({ name, email });
  });
  return { patients, problems };
}
